import { memo, useRef, useEffect, useMemo } from 'react'
import { useThree, useFrame } from '@react-three/fiber'
import { PerspectiveCamera, Vector3, Euler } from 'three'
import { CamData } from '../utils'
import { useDragRotate } from '../hooks/useDragRotate'

const WIDTH_LOCK = 0.5
const TARGET_ASPECT = 16 / 9
const FAR = 1000

// Scratch objects reused every frame to avoid per-frame allocations. Safe as
// module singletons because exactly one Camera is mounted and useFrame is sync.
const _worldPos = new Vector3()
const _forward = new Vector3()
const _euler = new Euler(0, 0, 0, 'YXZ')

interface CameraProps {
  cameras: CamData[]
  activeCam: number
  dragEnabled: boolean
}

function Camera({ cameras, activeCam, dragEnabled }: CameraProps) {
  const { set, size } = useThree()
  const cameraRef = useRef<PerspectiveCamera>(null)
  const active = cameras[activeCam]
  const drag = useDragRotate(dragEnabled, active?.maxR)
  const aspect = size.width / size.height

  const prevCam = useRef(activeCam)

  useEffect(() => {
    if (cameraRef.current) {
      set({ camera: cameraRef.current })
    }
  }, [set])

  const fov = useMemo(() => {
    if (!active) return 50
    const halfVFov = (active.fov * Math.PI) / 360
    const corrected = (2 * Math.atan(Math.tan(halfVFov) * TARGET_ASPECT / aspect) * 180) / Math.PI
    return active.fov + (corrected - active.fov) * WIDTH_LOCK
  }, [active, aspect])

  useEffect(() => {
    const camera = cameraRef.current
    if (!camera || !active) return
    camera.fov = fov
    camera.aspect = aspect
    camera.near = active.near
    camera.far = FAR
    camera.updateProjectionMatrix()
  }, [active, fov, aspect])

  useFrame((_, delta) => {
    if (prevCam.current !== activeCam) {
      drag.reset()
      prevCam.current = activeCam
    }

    const { yaw: dragYaw, pitch: dragPitch } = drag.update(delta)

    if (cameraRef.current && active) {
      const obj = active.object
      obj.getWorldPosition(_worldPos)
      cameraRef.current.position.copy(_worldPos)

      const forward = _forward.set(0, 1, 0).applyQuaternion(obj.quaternion)
      const yaw = Math.atan2(-forward.x, -forward.z)
      const pitch = Math.asin(forward.y)
      cameraRef.current.quaternion.setFromEuler(_euler.set(pitch + dragPitch, yaw + dragYaw, 0))
    }
  })

  return (
    <perspectiveCamera
      ref={cameraRef}
      aspect={aspect}
    />
  )
}

export default memo(Camera)
